import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef,
    useMemo,
} from 'react';
import SimplePeer from 'simple-peer';
import {useChatContext} from './context';
import {openChatWithUser} from './globalChatManager';

export type CallState = 'idle' | 'calling' | 'receiving' | 'in-call' | 'error' | 'failed';

interface CallError {
    type: 'media' | 'connection' | 'permission';
    message: string;
}

interface CallContextType {
    state: CallState;
    peer: SimplePeer.Instance | null;
    remoteStream: MediaStream | null;
    localStream: MediaStream | null;
    startCall: (callee: string) => void;
    acceptCall: () => void;
    rejectCall: () => void;
    endCall: () => void;
    incomingCaller?: string;
    incomingSignal?: any;
    callError: CallError | null;
    isMuted: boolean;
    toggleMute: () => void;
    callStartedAt: number | null;
    activePeer: string | null;
}

const CallContext = createContext<CallContextType | null>(null);
export const useCallContext = () => {
    const ctx = useContext(CallContext);
    if (!ctx) throw new Error('CallContext not found');
    return ctx;
};

export const CallContextProvider = ({children}: {children: React.ReactNode}) => {
    const {stompClient, currentUser} = useChatContext();

    const [state, setState] = useState<CallState>('idle');
    const stateRef = useRef<CallState>('idle');
    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    const [peer, setPeer] = useState<SimplePeer.Instance | null>(null);
    const peerRef = useRef<SimplePeer.Instance | null>(null);
    useEffect(() => {
        peerRef.current = peer;
    }, [peer]);

    const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
    const [localStream, setLocalStream] = useState<MediaStream | null>(null);
    const [incomingCaller, setIncomingCaller] = useState<string | undefined>();
    const [incomingSignal, setIncomingSignal] = useState<any | undefined>();
    const [activeCallee, setActiveCallee] = useState<string | undefined>();
    const [callError, setCallError] = useState<CallError | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [callStartedAt, setCallStartedAt] = useState<number | null>(null);

    const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
    const endedRef = useRef(false);

    useEffect(() => {
        if (!remoteAudioRef.current) {
            remoteAudioRef.current = document.createElement('audio');
            remoteAudioRef.current.autoplay = true;
            (remoteAudioRef.current as any).playsInline = true;
        }
    }, []);
    useEffect(() => {
        if (remoteAudioRef.current && remoteStream) {
            remoteAudioRef.current.srcObject = remoteStream;
            remoteAudioRef.current.play().catch((e) => console.warn('Autoplay block:', e));
        }
    }, [remoteStream]);

    const ICE_SERVERS = useMemo<RTCIceServer[]>(
        () => [
            {
                urls: [
                    'stun:coturn-dev.report-pro.digital:3478',
                    // , 'stun:stun.l.google.com:19302'
                ],
            },
            {
                urls: [
                    'turns:coturn-dev.report-pro.digital:5349?transport=tcp',
                    'turn:coturn-dev.report-pro.digital:3478?transport=tcp',
                    'turn:coturn-dev.report-pro.digital:3478?transport=udp',
                ],
                username: 'prochat',
                credential: 'prochat123!',
            },
        ],
        [],
    );

    const earlyIceRef = useRef<any[]>([]);
    const iceHandler = (signal: any) => {
        if (!peerRef.current) {
            earlyIceRef.current.push(signal);
            return;
        }
        try {
            peerRef.current.signal(signal);
        } catch {}
    };

    const sendCallSignal = useCallback(
        (type: 'offer' | 'answer' | 'ice-candidate' | 'hangup', targetUser: string, data: any) => {
            if (!currentUser || !stompClient?.connected) return;
            const destination = `/app/call/${targetUser}/${type}`;
            const payload = {from: currentUser.username, signal: data ?? null};
            try {
                stompClient.publish({destination, body: JSON.stringify(payload)});
            } catch {}
        },
        [stompClient, currentUser],
    );

    const toggleMute = useCallback(() => {
        if (!localStream) return;
        localStream.getAudioTracks().forEach((t) => (t.enabled = !t.enabled));
        setIsMuted((v) => !v);
    }, [localStream]);

    const cleanupMedia = useCallback(() => {
        if (endedRef.current) return;
        endedRef.current = true;
        try {
            peerRef.current?.destroy();
        } catch {}
        localStream?.getTracks().forEach((t) => t.stop());
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;

        setPeer(null);
        peerRef.current = null;
        setLocalStream(null);
        setRemoteStream(null);
        setIncomingCaller(undefined);
        setIncomingSignal(undefined);
        setActiveCallee(undefined);
        earlyIceRef.current = [];
        setIsMuted(false);
        setCallError(null);
        setState('idle');
        stateRef.current = 'idle';
        setCallStartedAt(null);
        setTimeout(() => (endedRef.current = false), 0);
    }, [localStream]);

    const endCall = useCallback(() => {
        const targetUser = activeCallee || incomingCaller;
        cleanupMedia();
        if (targetUser) sendCallSignal('hangup', targetUser, null);
    }, [activeCallee, incomingCaller, cleanupMedia, sendCallSignal]);

    const endCallRef = useRef(endCall);
    useEffect(() => {
        endCallRef.current = endCall;
    }, [endCall]);

    // Таймаут установления p2p (25с)
    const setupConnectTimeout = useCallback(
        (targetUser: string) => {
            const timer = setTimeout(() => {
                if (stateRef.current !== 'in-call') {
                    sendCallSignal('hangup', targetUser, {reason: 'timeout'});
                    cleanupMedia();
                    setCallError({type: 'connection', message: 'timeout'});
                    setTimeout(() => setCallError(null), 3000);
                }
            }, 25000);
            return () => clearTimeout(timer);
        },
        [sendCallSignal, cleanupMedia],
    );

    const attachPeerHandlers = useCallback(
        (p: SimplePeer.Instance, otherUser: string) => {
            const cancelTimeout = setupConnectTimeout(otherUser);

            p.on('stream', (s) => setRemoteStream(s));
            p.on('connect', () => {
                setState('in-call');
                stateRef.current = 'in-call';
                cancelTimeout?.();
                openChatWithUser(otherUser);
                setCallStartedAt(Date.now());
            });
            p.on('close', () => {
                cancelTimeout?.();
                endCallRef.current();
            });
            p.on('error', (err) => {
                console.error('Peer error:', err);
                cancelTimeout?.();
                setCallError({type: 'connection', message: 'Connection failed'});
                setState('failed');
                stateRef.current = 'failed';
            });

            return () => cancelTimeout?.();
        },
        [setupConnectTimeout],
    );

    const startCall = useCallback(
        async (callee: string) => {
            try {
                if (stateRef.current !== 'idle') return;
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: {echoCancellation: true, noiseSuppression: true, autoGainControl: true},
                });
                setLocalStream(stream);

                const p = new SimplePeer({
                    initiator: true,
                    trickle: true,
                    stream,
                    config: {iceServers: ICE_SERVERS},
                });

                let sentOffer = false;
                const iceQueue: any[] = [];

                p.on('signal', (data: any) => {
                    if (data.type === 'offer') {
                        sendCallSignal('offer', callee, data);
                        sentOffer = true;
                        while (iceQueue.length)
                            sendCallSignal('ice-candidate', callee, iceQueue.shift());
                    } else if (data.type === 'answer') {
                        sendCallSignal('answer', callee, data);
                    } else if ('candidate' in data) {
                        if (!sentOffer) iceQueue.push(data);
                        else sendCallSignal('ice-candidate', callee, data);
                    }
                });

                attachPeerHandlers(p, callee);

                setPeer(p);
                peerRef.current = p;
                setActiveCallee(callee);
                setState('calling');
                stateRef.current = 'calling';
            } catch (e) {
                console.error('getUserMedia error:', e);
                setCallError({type: 'media', message: 'Cannot access microphone'});
                setState('error');
                stateRef.current = 'error';
            }
        },
        [ICE_SERVERS, sendCallSignal, attachPeerHandlers],
    );

    const acceptCall = useCallback(async () => {
        if (!incomingSignal || !incomingCaller) return;
        try {
            if (stateRef.current !== 'receiving') return;
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            setLocalStream(stream);

            const p = new SimplePeer({
                initiator: false,
                trickle: true,
                stream,
                config: {iceServers: ICE_SERVERS},
            });

            p.on('signal', (data: any) => {
                if (data.type === 'offer' || data.type === 'answer') {
                    sendCallSignal(data.type, incomingCaller, data);
                } else if ('candidate' in data) {
                    sendCallSignal('ice-candidate', incomingCaller, data);
                }
            });

            attachPeerHandlers(p, incomingCaller);

            p.signal(incomingSignal);
            earlyIceRef.current.splice(0).forEach((sig) => {
                try {
                    p.signal(sig);
                } catch {}
            });

            setPeer(p);
            peerRef.current = p;
            setActiveCallee(incomingCaller);
        } catch (e) {
            console.error('getUserMedia error:', e);
            setCallError({type: 'media', message: 'Cannot access microphone'});
            setState('error');
            stateRef.current = 'error';
        }
    }, [incomingSignal, incomingCaller, ICE_SERVERS, sendCallSignal, attachPeerHandlers]);

    const rejectCall = useCallback(() => {
        if (incomingCaller) sendCallSignal('hangup', incomingCaller, null);
        setIncomingCaller(undefined);
        setIncomingSignal(undefined);
        setState('idle');
        stateRef.current = 'idle';
    }, [incomingCaller, sendCallSignal]);

    const queueBase = useMemo(
        () =>
            currentUser?.username ? `/user/${currentUser.username}/queue/call` : `/user/queue/call`,
        [currentUser?.username],
    );

    const unsubRef = useRef<(() => void) | null>(null);

    const subscribeAll = useCallback(() => {
        if (!stompClient?.connected) return;
        if (unsubRef.current) return;

        const offerSub = stompClient.subscribe(`${queueBase}/offer`, (msg) => {
            const {from, signal} = JSON.parse(msg.body);
            if (stateRef.current !== 'idle') {
                sendCallSignal('hangup', from, {reason: 'busy'});
                return;
            }
            setIncomingCaller(from);
            setIncomingSignal(signal);
            setState('receiving');
            stateRef.current = 'receiving';
        });

        const answerSub = stompClient.subscribe(`${queueBase}/answer`, (msg) => {
            const {signal} = JSON.parse(msg.body);
            if (stateRef.current !== 'calling') return;
            peerRef.current?.signal(signal);
        });

        const iceSub = stompClient.subscribe(`${queueBase}/ice-candidate`, (msg) => {
            const {signal} = JSON.parse(msg.body);
            iceHandler(signal);
        });

        const hangSub = stompClient.subscribe(`${queueBase}/hangup`, (msg) => {
            let reason: string | undefined;
            try {
                reason = JSON.parse(msg.body)?.signal?.reason;
            } catch {}
            if (reason === 'busy') {
                cleanupMedia();
                setCallError({type: 'connection', message: 'busy'});
                setTimeout(() => setCallError(null), 3000);
                return;
            }
            endCallRef.current();
        });

        unsubRef.current = () => {
            try {
                offerSub.unsubscribe();
            } catch {}
            try {
                answerSub.unsubscribe();
            } catch {}
            try {
                iceSub.unsubscribe();
            } catch {}
            try {
                hangSub.unsubscribe();
            } catch {}
        };
    }, [stompClient, queueBase, sendCallSignal, cleanupMedia]);

    useEffect(() => {
        if (!stompClient) return;
        if (stompClient.connected) subscribeAll();

        const orig = stompClient.onConnect;
        stompClient.onConnect = (frame) => {
            orig?.(frame);
            subscribeAll();
        };

        return () => {
            stompClient.onConnect = orig;
            unsubRef.current?.();
            unsubRef.current = null;
        };
    }, [stompClient, queueBase, subscribeAll]);

    const activePeer = useMemo(
        () => activeCallee ?? incomingCaller ?? null,
        [activeCallee, incomingCaller],
    );

    return (
        <CallContext.Provider
            value={{
                state,
                peer,
                remoteStream,
                localStream,
                startCall,
                acceptCall,
                rejectCall,
                endCall,
                incomingCaller,
                incomingSignal,
                callError,
                isMuted,
                toggleMute,
                callStartedAt,
                activePeer,
            }}
        >
            {children}
        </CallContext.Provider>
    );
};
